import { jsonToHtml } from '@contentstack/json-rte-serializer';
import parse, { domToReact } from 'html-react-parser';
import { HTMLAttributes } from 'react';
import QuickLink from '../quickLink';
import { useContent } from '../../../providers/content/hook';
import Image from '../../basic/image';
import {getRegionFromUri} from "../../../utils/regionUtils";
import { useTranslator } from '../../complex/header/locales-provider';
import { useUserProfile, UserProfileProps } from '../../../providers/user-provider';
import { InternalLinks } from '../../../common/enums/common';

interface JsonRTEProps extends HTMLAttributes<HTMLDivElement> {
  json: Record<string, any>;
  linkClassName?: string;
  openLinkInNewTab?: boolean,
  areaLabel?: string 
}

export default function JsonRTE({
  json,
  className,
  linkClassName,
  openLinkInNewTab,
  areaLabel,
  ...props
}: JsonRTEProps): JSX.Element {
  const { content } = useContent();
  const locales = useTranslator('global');
  const ParsedHtml = (): JSX.Element => {
    return parse(jsonToHtml(json), {
      replace: (domNode: any) => {
        const { attribs, name, children } = domNode;
        if (name === 'div' && attribs['data-type'] === 'hr') {
          const customBorderStyle = {
            borderTop: '1px solid #D8D8D8',
          };

          return <div style={customBorderStyle}></div>;
        }
        if (!attribs) {
          return;
        }
        if (name == 'iframe') {
          return (
            <p className="py-4 lg:w-[75%] xl:w-[60%] 2xl:w-[50%]">
              <iframe
                src={attribs?.src}
                width="100%"
                height={attribs?.height}
                title={attribs?.title}
                allow={attribs?.allow}
                referrerPolicy={attribs?.referrerpolicy}
              ></iframe>{' '}
            </p>
          );
        }
        if (attribs.href) {
          let isExternal = (attribs.target === '_blank' || attribs?.type === 'asset') ? true : false;
          if (attribs['data-sys-content-type-uid'] && attribs['data-sys-content-type-uid'] === 'site_map_link') {
            attribs.href = content?.siteMap?.SITE_MAP_LINK[attribs['data-sys-entry-uid']]?.url ;
            isExternal = content?.siteMap?.SITE_MAP_LINK[attribs['data-sys-entry-uid']]?.isExternal;
          }
          const transText = isExternal ? locales?.['aria.label.newTab'] : '';
          if (openLinkInNewTab) {
            isExternal = attribs['href'] && attribs['target'] === '_blank';
            const { userProfile } = useUserProfile();
            if (userProfile && attribs.href === InternalLinks.contactUs) {
              attribs.href = InternalLinks.secureContactUs;
            }
            if (!attribs.href?.toString()?.includes('http')) {
              attribs.href = '/' + getRegionFromUri() + attribs.href;
            }
          }
          return (
            <QuickLink
              href={attribs.href}
              className={`underline ${linkClassName}`}
              isExternal={isExternal}
              openLinkInNewTab={openLinkInNewTab}
              tabIndex={(domToReact(children) as string).length >0 ? 0 : -1}
              aria-label={ areaLabel && areaLabel.length > 0 ? areaLabel : (isExternal ? domToReact(children) + transText : domToReact(children) as string)}
            >
              {domToReact(children)}
            </QuickLink>
          );
        }
        if (attribs && attribs?.type == 'asset') {
          return (
            <p className="py-4">
              <Image
                src={attribs['data-sys-asset-filelink']}
                alt={attribs['data-sys-asset-alt']}
                layout={'responsive'}
                className="h-auto w-auto md:max-w-[400px]"
              />
            </p>
          );
        }
      },
    }) as JSX.Element;
  };

  if (!json) return <></>;
  return (
    <div
      data-testid="jsonRTE"
      {...props}
      className={`[&_ul]:ml-4 md:[&_ul]:ml-8 [&_ul]:list-disc [&_ol]:ml-4 md:[&_ol]:ml-8 [&_ol]:list-decimal [&_li]:m-3 [&_p]:py-1 ${className}`}
    >
      <ParsedHtml />
    </div>
  );
}
